import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import theme from "../theme";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import TicketTimerButton from "./TicketTimerButton";
import BranchChip from "./BranchChip";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";

const TicketCard = ({
  index,
  filteredTickets,
  staffID,
  ticket,
  tabStatus,
  currentView = "card",
  markForToday = () => {},
  markAsResolved = () => {},
  storeName,
}) => {
  const { userInfo, currentStoreInfo } = useContext(AutoTaskAuthContext) || {};

  // How many branches the ticket's store has, when we can know it.
  //
  // Only for rows belonging to the store this session is in — a cross-store
  // queue (Kumudu's) carries each row's store and branch NAME but nothing about
  // how many branches that store runs. Unknown counts as one, which suppresses
  // a branch that merely repeats its store's name; see branchLabelFor. That is
  // the right way to be wrong here: migration 015 named every store's first
  // branch after the store, so guessing the other way would print "TEST · TEST"
  // on every row in the list.
  const ticketBranchCount =
    Number(ticket.store_id) === Number(currentStoreInfo?.id)
      ? currentStoreInfo?.branch_count
      : undefined;

  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const descriptionRef = useRef(null);
  const currentStatusTab = tabStatus;

  const [expandedCard, setExpandedCard] = useState(false);
  const [maxHeight, setMaxHeight] = useState("0px");
  const [isOverflowing, setIsOverflowing] = useState(false);
  const toggleExpand = () => setExpandedCard((prev) => !prev);

  // is this a project ticket?
  const isProjectTicket = !!ticket.pm_id;

  useEffect(() => {
    if (descriptionRef.current) {
      const el = descriptionRef.current;
      const isContentOverflowing = el.scrollHeight > 20;
      setIsOverflowing(isContentOverflowing);

      if (expandedCard) {
        setMaxHeight(`${el.scrollHeight}px`);
      } else {
        setMaxHeight("20px");
      }
    }
  }, [ticket.description]);

  useEffect(() => {
    if (descriptionRef.current) {
      const el = descriptionRef.current;
      if (expandedCard) {
        setMaxHeight(`${el.scrollHeight}px`);
      } else {
        setMaxHeight("20px");
      }
    }
  }, [expandedCard]);

  function formatDateTimeInIndia(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
    });
    const formattedTime = date.toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${formattedDate}, ${formattedTime}`;
  }

  const dateAndTime = formatDateTimeInIndia(ticket.start_date);

  const handleSelctedTicket = (data) => {
    navigate("/ticket-panel", { state: { data, filteredTickets, index } });
  };

  // ─── Color helpers ────────────────────────────────────────────────────────────
  // bg & border change when it's a project (pm_id) ticket
  const cardBg = () => {
    if (ticket.status === "resolved") return "#F5FFF9";
    if (isProjectTicket) return "#FFFBF3"; // soft purple for project tickets
    if (ticket.status === "in_progress") return "#FFFBF3";
    return "white";
  };

  const cardBorder = () => {
    if (ticket.status === "resolved") return "1px solid #0E830A";
    if (isProjectTicket) return "1px solid #F7B733"; // purple border for project
    if (ticket.status === "in_progress") return "1px solid #F7B733";
    return `1px solid ${theme.lightTheme.placeholder}`;
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // Card view
  if (currentView === "card" || isMobile) {
    return (
      <Box
        ref={cardRef}
        onClick={() => handleSelctedTicket(ticket)}
        bgcolor={cardBg()}
        sx={{
          borderRadius: 3,
          border: cardBorder(),
          px: 0.8,
          py: 1,
          cursor: "pointer",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
        }}
      >
        {/* Ticket Priority Indicator */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            alignSelf: "stretch",
            mr: 1,
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: theme.lightTheme.prioritieIndicators[ticket.priority],
              flexShrink: 0,
            }}
          />
        </Box>

        {/* Details Parent Flex */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          {/* Header Container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
              gap: 1,
            }}
          >
            {/* Assignee (follow_up tab) */}
            {currentStatusTab === "follow_up" && (
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                <span style={{ fontSize: 12, fontWeight: 400 }}>
                  Assignee -{" "}
                </span>
                {ticket.assigned_to_name}
              </Typography>
            )}

            {/* Project badge */}
            {/* {isProjectTicket && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.4,
                  bgcolor: "#EDE9FE",
                  border: "1px solid #F7B733",
                  borderRadius: 1,
                  px: 0.8,
                  py: 0.2,
                  alignSelf: "flex-start",
                }}
              >
                <FolderSpecialRoundedIcon
                  sx={{ fontSize: 12, color: "#F7B733" }}
                />
                <Typography
                  sx={{ fontSize: 10, color: "#000000", fontWeight: 600 }}
                >
                  Project
                </Typography>
              </Box>
            )} */}

            {/* Header Child Flex */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* Header Ticket Info Container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                {/* Store Name & Raised By */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 0.25,
                    width: "100%",
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                      title={`${ticket.store_id} - ${ticket.store_name}`}
                    >
                      {ticket.store_id} - {ticket.store_name}
                    </Typography>
                    {/* Where the ticket was RAISED. Absent on most rows by
                        design — see BranchChip — so nothing here reserves space
                        for it. */}
                    <BranchChip
                      row={ticket}
                      storeName={ticket.store_name ?? storeName}
                      branchCount={ticketBranchCount}
                      dense
                    />
                  </Box>
                  <Typography sx={{ fontSize: 12 }}>
                    {ticket.created_by_name || "Self"}
                  </Typography>
                </Box>

                {/* Department Name | Ticket Number & Ticket Raised Time */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: theme.lightTheme.ticketCard.heading.secondary,
                    }}
                  >
                    {ticket.department_name} | {ticket.ticket_number}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: theme.lightTheme.ticketCard.date,
                    }}
                  >
                    {dateAndTime}
                  </Typography>
                </Box>
              </Box>

              {/* Today & Resolved Icons */}
              {(currentStatusTab === "today" ||
                currentStatusTab === "resolved") && (
                <Box
                  sx={{
                    ml: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {currentStatusTab === "today" && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        bgcolor: "#FFECC4",
                        border: "4px solid #FFF2D6",
                        boxShadow: "inset 0px -2px 4px #F7B733",
                      }}
                    >
                      <HistoryRoundedIcon
                        sx={{ fontSize: 18, fill: "#FFAC00" }}
                      />
                    </Box>
                  )}
                  {currentStatusTab === "resolved" && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        bgcolor: "#24B567",
                        border: "4px solid #C4FFDF",
                        boxShadow: "inset 0px -1px 4px #00000040",
                      }}
                    >
                      <DoneRoundedIcon sx={{ fontSize: 18, fill: "white" }} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Today & Resolved Buttons — shown for in_progress AND project tabs */}
            {!staffID &&
              (currentStatusTab === "in_progress" ||
                currentStatusTab === "project" ||
                currentStatusTab === "today") && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {/* Work timer quick start/stop */}
                    <TicketTimerButton ticket={ticket} compact />

                    {/* Today button — shown for in_progress and project (not today tab) */}
                    {(currentStatusTab === "in_progress" ||
                      currentStatusTab === "project") && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #F7B733",
                          borderRadius: 1.5,
                          color: "black",
                          px: 1.5,
                          height: 30,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          markForToday(ticket);
                        }}
                      >
                        <Typography sx={{ fontSize: 12 }}>Today</Typography>
                      </Box>
                    )}

                    {/* Resolve button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #0E830A",
                        borderRadius: 1.5,
                        color: "black",
                        px: 1.5,
                        py: 0.75,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsResolved(ticket);
                      }}
                    >
                      <Typography sx={{ fontSize: 12 }}>Resolve</Typography>
                    </Box>
                  </Box>
                </Box>
              )}

            <Box
              sx={{
                display:
                  staffID || currentStatusTab === "resolved" ? "flex" : "block",
                gap: staffID ? 0.4 : 0,
                alignItems: staffID ? "center" : "",
                justifyContent:
                  staffID || currentStatusTab === "resolved"
                    ? "space-between"
                    : "",
                width:
                  staffID || currentStatusTab === "resolved" ? "100%" : "auto",
              }}
            >
              {ticket.assigned_to !== ticket.assigned_from && (
                <Typography
                  sx={{
                    textAlign: "end",
                    fontSize: 10,
                    color: theme.lightTheme.ticketCard.date,
                  }}
                >
                  {ticket.assigner_name}
                </Typography>
              )}
              {ticket.time_budget && (
                <Typography
                  sx={{
                    textAlign: "end",
                    fontSize: 10,
                    color: theme.lightTheme.ticketCard.date,
                  }}
                >
                  {ticket.time_budget} min
                </Typography>
              )}
            </Box>
          </Box>

          {/* Expandable Description */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              cursor: isOverflowing ? "pointer" : "default",
              mt: 1,
              display: "flex",
              alignItems: "flex-start",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isOverflowing) toggleExpand();
            }}
          >
            <Box
              ref={descriptionRef}
              sx={{
                flex: 1,
                fontSize: 11,
                overflow: "hidden",
                maxHeight: maxHeight,
                transition: "max-height 0.5s ease",
                pr: 3,
                color: "#000",
                lineHeight: "20px",
                position: "relative",
              }}
            >
              {ticket.description}
            </Box>

            {isOverflowing && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 24,
                  color: theme.lightTheme.placeholder,
                  transform: expandedCard ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // List View
  return (
    <Box
      ref={cardRef}
      onClick={() => handleSelctedTicket(ticket)}
      bgcolor={cardBg()}
      sx={{
        borderRadius: 2,
        border: cardBorder(),
        px: 1,
        py: 0.6,
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* Priority dot */}
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: theme.lightTheme.prioritieIndicators[ticket.priority],
          flexShrink: 0,
          mt: 0.4,
        }}
      />

      {/* Project badge (list view) */}
      {isProjectTicket && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.3,
            bgcolor: "#EDE9FE",
            border: "1px solid #7C3AED",
            borderRadius: 1,
            px: 0.6,
            flexShrink: 0,
          }}
        >
          <FolderSpecialRoundedIcon sx={{ fontSize: 10, color: "#7C3AED" }} />
          <Typography sx={{ fontSize: 9, color: "#7C3AED", fontWeight: 600 }}>
            Project
          </Typography>
        </Box>
      )}

      {/* Store & Basic Info */}
      <Typography
        sx={{
          fontSize: 12,
          flex: "0 1 150px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={`${ticket.store_id} - ${ticket.store_name}`}
      >
        {ticket.store_id} -{" "}
        {ticket.store_name === undefined ? storeName : ticket.store_name}
      </Typography>

      {/* The raising branch, beside the store it belongs to. Collapses to
          nothing on a single-branch store, so the row keeps its spacing. */}
      <BranchChip
        row={ticket}
        storeName={
          ticket.store_name === undefined ? storeName : ticket.store_name
        }
        branchCount={ticketBranchCount}
        dense
      />

      {/* Department | Ticket no */}
      <Typography
        sx={{
          fontSize: 12,
          color: theme.lightTheme.ticketCard.heading.secondary,
          minWidth: 120,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {ticket.department_name} | {ticket.ticket_number}
      </Typography>

      {/* Created by */}
      <Typography sx={{ fontSize: 12, minWidth: 70, textAlign: "center" }}>
        {ticket.created_by_name || "Self"}
      </Typography>

      {/* Date */}
      <Typography
        sx={{
          fontSize: 10,
          color: theme.lightTheme.ticketCard.date,
          minWidth: 90,
          textAlign: "center",
        }}
      >
        {dateAndTime}
      </Typography>

      {/* Description (expandable inline) */}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          if (isOverflowing) toggleExpand();
        }}
        sx={{
          flex: 2,
          position: "relative",
          cursor: isOverflowing ? "pointer" : "default",
          display: "flex",
        }}
      >
        <Typography
          ref={descriptionRef}
          sx={{
            fontSize: 11,
            overflow: "hidden",
            maxHeight: maxHeight,
            transition: "max-height 0.5s ease",
            color: "#000",
            lineHeight: "18px",
            pr: isOverflowing ? 3 : 0,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {ticket.description}
        </Typography>
        {isOverflowing && (
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: theme.lightTheme.placeholder,
              transform: expandedCard ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
              ml: -2,
            }}
          />
        )}
      </Box>

      {/* Today & Resolve Buttons — shown for in_progress, project and today tabs */}
      {!staffID &&
        (currentStatusTab === "in_progress" ||
          currentStatusTab === "project" ||
          currentStatusTab === "today") && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 80,
              justifyContent: "flex-end",
            }}
          >
            {/* Work timer quick start/stop */}
            <TicketTimerButton ticket={ticket} compact />

            {/* Today button — in_progress and project only */}
            {(currentStatusTab === "in_progress" ||
              currentStatusTab === "project") && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #F7B733",
                  borderRadius: 1.5,
                  color: "black",
                  px: 1,
                  py: 0.5,
                  fontSize: 10,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  markForToday(ticket);
                }}
              >
                Today
              </Box>
            )}

            {/* Resolve button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #0E830A",
                borderRadius: 1.5,
                color: "black",
                px: 1,
                py: 0.5,
                fontSize: 10,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                markAsResolved(ticket);
              }}
            >
              Resolve
            </Box>
          </Box>
        )}

      {/* Time & Assigner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          minWidth: 70,
          justifyContent: "flex-end",
        }}
      >
        {ticket.assigned_to !== ticket.assigned_from && (
          <Typography
            sx={{
              fontSize: 10,
              color: theme.lightTheme.ticketCard.date,
            }}
          >
            {ticket.assigner_name}
          </Typography>
        )}
        {ticket.time_budget && (
          <Typography
            sx={{
              fontSize: 10,
              color: theme.lightTheme.ticketCard.date,
            }}
          >
            {ticket.time_budget} min
          </Typography>
        )}
      </Box>

      {/* Status icon */}
      {(currentStatusTab === "today" || currentStatusTab === "resolved") && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: "50%",
            bgcolor: currentStatusTab === "resolved" ? "#24B567" : "#FFECC4",
            border:
              currentStatusTab === "resolved"
                ? "3px solid #C4FFDF"
                : "3px solid #FFF2D6",
          }}
        >
          {currentStatusTab === "resolved" ? (
            <DoneRoundedIcon sx={{ fontSize: 14, color: "white" }} />
          ) : (
            <HistoryRoundedIcon sx={{ fontSize: 14, fill: "#FFAC00" }} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default TicketCard;
